/**
 * The colour arithmetic the kit page runs on: is this fill dark, what does a `var:` background point at,
 * and what is the canonical hex for a value a human typed.
 *
 * Lifted out of `routes/brand-kits_.$kitId.tsx` unchanged. It moved because the agent's kit tools have to
 * make the SAME calls the panels make — a variation background the agent sets and one a click sets must
 * resolve identically, or the two writers disagree about which mark reads on which ground.
 */
import type { MoodboardProfile } from '../types/presentation'
import { gradientCssToStops } from '~/design-system/components/ColorSelector/gradientCss'

/** "#abc", "abc123", "#AABBCC" → "#aabbcc"; anything else → null. */
export function normalizeHex(raw: string): string | null {
  const value = raw.trim().replace(/^#/, '').toLowerCase()
  if (/^[0-9a-f]{3}$/.test(value)) return `#${value[0]}${value[0]}${value[1]}${value[1]}${value[2]}${value[2]}`
  if (/^[0-9a-f]{6}$/.test(value)) return `#${value}`
  return null
}

/** WCAG-ish relative luminance; below the midpoint counts as a dark background. */
export function isDarkBg(hex: string): boolean {
  const normalized = normalizeHex(hex)
  if (!normalized) return false
  const r = parseInt(normalized.slice(1, 3), 16)
  const g = parseInt(normalized.slice(3, 5), 16)
  const b = parseInt(normalized.slice(5, 7), 16)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128
}

/**
 * A variation background may REFERENCE one of the kit's colour roles ("var:accent") instead of holding a
 * hard colour. Resolved at render, so editing the role in Colors recolours every card that points at it.
 */
export function resolveVariationBg(background: string, profile: MoodboardProfile): string {
  if (!background.startsWith('var:')) return background
  return profile.roles?.[background.slice(4)] ?? '#e5e5e5'
}

/** Dark or light for ANY stored fill: hex, rgba, or a gradient (judged by its first stop, the colour the
 *  label actually sits near). */
export function valueIsDark(value: string): boolean {
  const gradient = gradientCssToStops(value)
  if (gradient) return gradient.stops.length > 0 ? isDarkBg(gradient.stops[0].color) : false
  const rgba = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (rgba) return 0.2126 * +rgba[1] + 0.7152 * +rgba[2] + 0.0722 * +rgba[3] < 128
  return isDarkBg(value)
}
